import React, { lazy, FC, Suspense } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'
import { MasterLayout } from '../../_metronic/layout/MasterLayout'
import { getCSSVariableValue } from '../../_metronic/assets/ts/_utils'
import { Registration } from '../modules/auth/sub-pages/Registration'
import { Board } from '../modules/boards/sub-pages/Board'
import { useSelector } from 'react-redux'
import { useAuth } from '../modules/auth'
import { Templates } from '../modules/templates/Templates'
import TopBarProgress from 'react-topbar-progress-indicator'
import UsersPage from '../modules/user-management/UsersPage'
import { CreateOrganization } from '../modules/organization/CreateOrganization'
// import Plan from '../modules/billing/sub-pages/Plan'
import Plans from '../modules/auth/sub-pages/Plans'
import AuthLayout from '../modules/auth/AuthLayout'
import FeedBack from '../pages/feedback'
import { VerifyEmailChange } from '../modules/auth/sub-pages/VerifyEmailChange'
import { VerifyOrgEmailChange } from '../modules/auth/sub-pages/VerifyOrgEmailChange'
import { SuperAdminModeProvider } from '../modules/super-admin/context/SuperAdminModeContext'

// Declare lazy components at module scope so their component identity is stable
// across renders. Calling lazy() inside the component body produced a brand-new
// component type on every render, so any PrivateRoutes re-render (e.g. a
// setCurrentUser from useGetSubscriptionDetails, or the isSubscription
// cancel->active flip on upgrading an expired account) remounted the entire
// matched route subtree. That remount re-ran BillingTabs' mount effect, which
// called subscriptionHandler -> setCurrentUser -> re-render -> remount, a loop
// that surfaced as React error #185 ("Maximum update depth exceeded").
const BuilderPageWrapper = lazy(() => import('../pages/layout-builder/BuilderPageWrapper'))
const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
const UpgradePage = lazy(() => import('../modules/upgrade/UpgradePage'))
const BillingPage = lazy(() => import('../modules/billing/BillingPage'))
const BoardPage = lazy(() => import('../modules/boards/BoardPage'))
const Pages = lazy(() => import('../modules/pages/Pages'))
const HomePage = lazy(() => import('../modules/home/HomePage'))
const ManageAccountPage = lazy(() => import('../modules/manage-account/ManageAccountPage'))
const IntegrationsPage = lazy(() => import('../modules/integrations/IntegrationsPage'))
const ManageContactsPage = lazy(() => import('../modules/manage-contacts/ManageContactsPage'))
const MyRemindersPage = lazy(() => import('../modules/my-reminders/MyRemindersPage'))
const SuperAdminPage = lazy(() => import('../modules/super-admin/SuperAdminPage'))

const PrivateRoutes: React.FC = () => {
  const subscriptionStore = useSelector((state: any) => state.subscriptionReducer)
  const { isSubscription } = subscriptionStore?.isSubscription
  const { currentUser } = useAuth()

  const { isVerified, isInvitedByOrganization , role } = currentUser?.data.user
  const { hasOrganization } = currentUser
  const isSuperAdmin = !!role?.isSuperAdmin
  const urlAddress = window.location.search
  const token = urlAddress.slice(7)
  const NavigateToHome = (): React.JSX.Element => {
    if (token.length > 0) {
      return <Navigate to={`/home?token=${token}`} />;
    } else {
      return <Navigate to="/home" />;
    }
  };

  // ---- Super Admin: only admin routes ----
  if (isSuperAdmin) {
    return (
      <SuperAdminModeProvider>
        <Routes>
          <Route element={<MasterLayout />}>
            <Route
              path='admin/*'
              element={
                <SuspensedView>
                  <SuperAdminPage />
                </SuspensedView>
              }
            />
            {/* Redirect everything else to admin dashboard */}
            <Route path='*' element={<Navigate to='/admin/dashboard' />} />
          </Route>
        </Routes>
      </SuperAdminModeProvider>
    )
  }

  // ---- Regular User: only user routes ----
  return (
    <SuperAdminModeProvider>
      <Routes>
        {/* The verification entry renders in the auth layout — Plans logs the
            user out on mount and reloads, so subsequent navigation happens in
            the unauthenticated AuthPage routes. */}
        <Route element={<AuthLayout />}>
          <Route path='auth/email-verification' element={<Plans />} />
        </Route>
        <Route element={<MasterLayout />}>
          <Route path='builder' element={<BuilderPageWrapper />} />
          <Route path='auth/' element={<NavigateToHome />} />
          <Route path='auth/invite' element={<NavigateToHome />} />
          <Route path='auth/organization-invitation' element={<NavigateToHome />} />

          <Route path='auth/verify-email/*' element={<Navigate to='/error/AlreadyLogin' />} />
          <Route path='auth/email-change/verify' element={<VerifyEmailChange />} />
          <Route path='auth/organization-email-change/verify' element={<VerifyOrgEmailChange />} />

          {isSubscription === 'active' || isSubscription === undefined ? (
            <>
              <Route path='auth/registration' element={<Registration />} />
              <Route path='/board-view/board/:boardId' element={<Board />} />

              <Route
                path='docusign/sign'
                element={
                  <SuspensedView>
                    <HomePage />
                  </SuspensedView>
                }
              />
              <Route
                path='home/*'
                element={
                  <SuspensedView>
                    <HomePage />
                  </SuspensedView>
                }
              />

              <Route
                path='board/*'
                element={
                  <SuspensedView>
                    <BoardPage />
                  </SuspensedView>
                }
              />
              <Route
                path='notes/:boardId'
                element={
                  <SuspensedView>
                    <Pages />
                  </SuspensedView>
                }
              />
              <Route
                path='account/upgrade'
                element={
                  <SuspensedView>
                    <UpgradePage/>
                  </SuspensedView>
                }
              />
              {role?.isGuest !== true && <Route
                path='templates/*'
                element={
                  <SuspensedView>
                    <Templates />
                  </SuspensedView>
                }
              />}
              {!isInvitedByOrganization && role?.isGuest !== true &&
                <Route
                  path='manage-account/*'
                  element={
                    <SuspensedView>
                      <ManageAccountPage />
                    </SuspensedView>
                  }
                />
              }
              {role?.isGuest !== true &&
                <Route
                  path='integrations/*'
                  element={
                    <SuspensedView>
                      <IntegrationsPage />
                    </SuspensedView>
                  }
                />
              }

              {/* {
        currentAccount.isCompany&& */}
             {role?.isGuest !== true && <Route
                path='manage-contacts/*'
                element={
                  <SuspensedView>
                    <ManageContactsPage />
                  </SuspensedView>
                }
              />}
              {role?.isGuest !== true && <Route
                path='my-reminders/*'
                element={
                  <SuspensedView>
                    <MyRemindersPage />
                  </SuspensedView>
                }
              />}
              {/* } */}
              {
                //eslint-disable-next-line
                !hasOrganization && !isInvitedByOrganization && role?.isGuest !== true && (
                  <Route
                    path='create'
                    element={
                      <SuspensedView>
                        <CreateOrganization />
                      </SuspensedView>
                    }
                  />
                )
              }

              <Route
                path='profile/*'
                element={
                  <SuspensedView>
                    <ProfilePage />
                  </SuspensedView>
                }
              />

             {role?.isGuest !== true && <Route
                path='/user-management/*'
                element={
                  <SuspensedView>
                    <UsersPage />
                  </SuspensedView>
                }
              />}
              <Route path='*' element={<Navigate to='/error/404' />} />
            </>
          ) : (
            isVerified && role?.isGuest !== true && <Route path='*' element={<Navigate to='/billing/billing-details' />} />
          )}

          {isVerified && role?.isGuest !== true && (
            <Route
              path='/billing/*'
              element={
                <SuspensedView>
                  <BillingPage />
                </SuspensedView>
              }
            />
            )}
            <Route
              path='/feedback'
              element={
                <SuspensedView>
                  <FeedBack />
                </SuspensedView>
              }
            />
        </Route>
      </Routes>
    </SuperAdminModeProvider>
  )
}

interface ComponentProps {
  children: React.ReactNode
}
const SuspensedView: FC<ComponentProps> = ({ children }) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export { PrivateRoutes }
